import { useState } from 'react';
import { supabase } from '../lib/supabaseClient';
import { Box, Card, CardContent, Typography, TextField, Button, Alert, Stack, MenuItem, Select, FormControl, InputLabel } from '@mui/material';
import TidioChat from '../components/TidioChat';
import { sanitizeInput, validateEmail, validatePassword, validatePhone, validateUsername, validateUserGroup } from '../lib/validation';

const USER_GROUPS = [
  'OS Team',
  'Show Support',
  'Show Management (Bay)',
  'Show Management (Padang)',
  'Artiste Management',
  'Log & Finance',
  'Ops. Safety and Contingencies'
] as const;

export default function SignUp() {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [hpNumber, setHpNumber] = useState('');
  const [userGroup, setUserGroup] = useState<string>('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    // Sanitize and validate inputs
    const sanitizedUsername = sanitizeInput(username);
    const sanitizedEmail = sanitizeInput(email);
    const sanitizedPassword = sanitizeInput(password);
    const sanitizedHpNumber = sanitizeInput(hpNumber);

    if (!validateUsername(sanitizedUsername)) {
      setError('Invalid username. Must be 3-20 characters and contain only letters, numbers, underscores, and hyphens.');
      setLoading(false);
      return;
    }
    if (!validateEmail(sanitizedEmail)) {
      setError('Invalid email address.');
      setLoading(false);
      return;
    }
    if (!validatePassword(sanitizedPassword)) {
      setError('Password must be at least 6 characters and contain only valid characters.');
      setLoading(false);
      return;
    }
    if (!validatePhone(sanitizedHpNumber)) {
      setError('Invalid phone number. Must be a valid Singapore number starting with 8 or 9.');
      setLoading(false);
      return;
    }
    if (!validateUserGroup(userGroup)) {
      setError('Please select a valid user group');
      setLoading(false);
      return;
    }

    try {
      // First, sign up the user with Supabase Auth
      const { data, error: signUpError } = await supabase.auth.signUp({
        email: sanitizedEmail,
        password: sanitizedPassword,
        options: {
          data: {
            username: sanitizedUsername,
            hp_number: sanitizedHpNumber,
            user_group: userGroup
          },
          emailRedirectTo: `${window.location.origin}/login`
        }
      });

      if (signUpError) throw signUpError;

      if (data?.user?.identities?.length === 0) {
        setError('A user with this email already exists');
      } else if (data.user) {
        // Add user to the users table
        const { error: insertError } = await supabase
          .from('users')
          .insert({
            id: data.user.id,
            username: sanitizedUsername,
            email: sanitizedEmail,
            hp_number: sanitizedHpNumber,
            user_group: userGroup,
            user_role: 'user' // Default role
          });

        if (insertError) {
          console.error('Error inserting user:', insertError);
          setError('Error creating user profile. Please try again.');
          // Sign out the user since we couldn't create their profile
          await supabase.auth.signOut();
        } else {
          setSuccess('Please check your email for the verification link');
          // Clear form
          setUsername('');
          setEmail('');
          setPassword('');
          setHpNumber('');
          setUserGroup('');
        }
      }
    } catch (err: any) {
      console.error('Signup error:', err);
      setError(err.message || 'An error occurred during signup');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <TidioChat />
      <Box minHeight="100vh" display="flex" alignItems="center" justifyContent="center" bgcolor="#f5f5f5" sx={{ p: { xs: 2, sm: 4 } }}>
        <Card sx={{ minWidth: { xs: '100%', sm: 350 }, p: { xs: 2, sm: 4 }, display: 'flex', flexDirection: 'column' }}>
          <CardContent>
            <Typography variant="h5" align="center" gutterBottom>Sign Up</Typography>
            <form onSubmit={handleSignUp}>
              <Stack spacing={2}>
                <TextField 
                  label="Username" 
                  value={username} 
                  onChange={e => setUsername(e.target.value)} 
                  required 
                  fullWidth 
                  error={!!error && error.includes('username')}
                  helperText={error && error.includes('username') ? error : '3-20 characters, letters, numbers, underscores, and hyphens only'}
                />
                <TextField 
                  label="Email" 
                  type="email" 
                  value={email} 
                  onChange={e => setEmail(e.target.value)} 
                  required 
                  fullWidth 
                  error={!!error && error.includes('email')}
                  helperText={error && error.includes('email') ? error : ''}
                />
                <TextField 
                  label="Password" 
                  type="password" 
                  value={password} 
                  onChange={e => setPassword(e.target.value)} 
                  required 
                  fullWidth 
                  error={!!error && error.includes('Password')}
                  helperText={error && error.includes('Password') ? error : 'At least 6 characters, letters, numbers, and basic special characters'}
                />
                <TextField 
                  label="HP Number" 
                  value={hpNumber} 
                  onChange={e => setHpNumber(e.target.value)} 
                  required 
                  fullWidth 
                  error={!!error && error.includes('phone')}
                  helperText={error && error.includes('phone') ? error : '8 digits starting with 8 or 9'}
                />
                <FormControl fullWidth required error={!!error && error.includes('user group')}>
                  <InputLabel>User Group</InputLabel>
                  <Select
                    value={userGroup}
                    label="User Group"
                    onChange={(e) => setUserGroup(e.target.value)}
                  >
                    {USER_GROUPS.map((group) => (
                      <MenuItem key={group} value={group}>
                        {group}
                      </MenuItem>
                    ))}
                  </Select>
                  {error && error.includes('user group') && (
                    <Typography color="error" variant="caption">{error}</Typography>
                  )}
                </FormControl>
                {error && <Alert severity="error">{error}</Alert>}
                {success && <Alert severity="success">{success}</Alert>}
                <Button 
                  type="submit" 
                  variant="contained" 
                  color="primary" 
                  disabled={loading} 
                  fullWidth
                >
                  {loading ? 'Signing up...' : 'Sign Up'}
                </Button>
              </Stack>
            </form>
          </CardContent>
        </Card>
      </Box>
    </>
  );
} 